/**
 * /recruitment/requisitions/:id/pipeline — the candidate pipeline board
 * (ADR-0012, NET-NEW screen — no FE precedent). Columns are the application
 * statuses; cards are the candidates applied to this requisition.
 *
 * Movement: rather than literal drag-and-drop (which needs a dnd dependency and
 * is hard to test under RTL), each card has an explicit "Move to ▾" control.
 * The backend allows ANY status→status move (Phase 1 D-6, no transition
 * whitelist), so this control exposes the full flexibility, testably.
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  listApplications,
  listCandidates,
  moveApplication,
  PIPELINE_STATUSES,
  type Application,
  type JobApplicationStatus,
} from '../api/recruitment.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

export const recruitmentPipelineRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/requisitions/$requisitionId/pipeline',
  component: RecruitmentPipelinePage,
});

const STATUS_LABEL: Record<JobApplicationStatus, string> = {
  applied: 'Applied',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

function RecruitmentPipelinePage(): JSX.Element {
  const { requisitionId } = recruitmentPipelineRoute.useParams();
  const apps = useQuery({
    queryKey: ['recruitment', 'applications', requisitionId],
    queryFn: () => listApplications({ requisitionId }),
  });
  const candidates = useQuery({ queryKey: ['recruitment', 'candidates'], queryFn: () => listCandidates() });

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of candidates.data?.candidates ?? []) m.set(c.id, `${c.firstName} ${c.lastName}`);
    return m;
  }, [candidates.data]);

  return (
    <section className="p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Pipeline</h1>
        <p className="text-sm text-gray-400">
          Move candidates through the funnel. <Link to="/recruitment/requisitions" className="text-gray-300 underline">← Requisitions</Link>
        </p>
      </header>

      {apps.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : apps.isError ? (
        <Placeholder error>Could not load the pipeline.</Placeholder>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE_STATUSES.map((status) => {
            const inColumn = (apps.data?.applications ?? []).filter((a) => a.status === status);
            return (
              <div key={status} className="min-w-[14rem] flex-shrink-0 space-y-2">
                <h2 className="text-xs uppercase tracking-wide text-gray-500 flex items-center justify-between">
                  <span>{STATUS_LABEL[status]}</span>
                  <span className="text-gray-600">{inColumn.length}</span>
                </h2>
                <div className="space-y-2">
                  {inColumn.map((app) => (
                    <PipelineCard key={app.id} app={app} name={nameById.get(app.candidateId) ?? app.candidateId} requisitionId={requisitionId} />
                  ))}
                  {inColumn.length === 0 ? <p className="text-xs text-gray-700 italic px-1">—</p> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PipelineCard({ app, name, requisitionId }: { app: Application; name: string; requisitionId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const move = useMutation({
    mutationFn: (status: JobApplicationStatus) => moveApplication(app.id, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications', requisitionId] }),
  });

  return (
    <div className="rounded border border-gray-800 bg-gray-950 p-3 space-y-2">
      <p className="text-sm text-white">{name}</p>
      <label className="block text-xs text-gray-400">
        <span className="sr-only">Move {name} to</span>
        <select
          aria-label={`Move ${name} to`}
          value=""
          onChange={(e) => {
            const next = e.target.value as JobApplicationStatus;
            if (next) move.mutate(next);
          }}
          disabled={move.isPending}
          className="w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-xs disabled:opacity-50"
        >
          <option value="">Move to…</option>
          {PIPELINE_STATUSES.filter((s) => s !== app.status).map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
      </label>
      <ApiErrorNotice error={move.error} fallback="Move failed." compact />
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
